// The board: six columns as the API places and orders them, the API's named views as filters,
// search, per repo sync state and the unreported count. On a phone the columns become tabs (tap or
// swipe). Labels take their colours from each meta repo's label roles (Board.repos[].labels).
import { useCallback, useEffect, useMemo, useRef, useState, type TouchEvent } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api, errorMessage, type Board, type BoardRepo, type Card, type ColumnKey, type MetaRepo, type ViewKey } from '../api/client';
import { COLUMN_TONE, RunMark, pulseClass } from '../components/Bits';
import { BatchBugs, CardView, cardKey } from '../components/CardView';
import { Icon } from '../components/Icon';
import { Shell } from '../components/Shell';
import { Callout, EmptyState, Select, buttonClass, cn, mono, selectBox } from '../components/ui';
import { LabelRolesByRepo } from '../lib/labels';
import { findWorkspace, rememberWorkspace, useSession } from '../lib/session';
import { ago } from '../lib/time';
import { useLoad } from '../lib/useLoad';
import { AgentsContext, useAgents } from '../lib/agents';
import { EmptySection, NotFoundPage } from './NotFoundPage';

// The filters are the API's named views (Board.views): their names, counts and which cards each
// holds come from the API. The view is asked for, never worked out here.
type View = ViewKey;
const SHIPPED_PAGE = 20;

// The owner filter (`owner`, a login) is asked for too; **Mine** is that filter with the
// signed-in user's linked GitHub login.
function viewQuery(repo: number | null, view: View, owner: string | null) {
  return { ...(repo ? { repo } : {}), ...(view !== 'all' ? { view } : {}), ...(owner ? { owner } : {}) };
}

/** The view's open columns (Shipped pages on its own): follow the cursor to the end. */
async function loadBoard(ws: number, repo: number | null, view: View, owner: string | null) {
  const query = { ...viewQuery(repo, view, owner), exclude: ['shipped' as const] };
  const first = await api.GET('/api/workspaces/{ws_id}/board', { params: { path: { ws_id: ws }, query } });
  if (!first.data) return first;
  let data: Board = first.data;
  while (data.next_cursor) {
    const more = await api.GET('/api/workspaces/{ws_id}/board', {
      params: { path: { ws_id: ws }, query: { ...query, cursor: data.next_cursor } },
    });
    if (!more.data) break;
    data = { ...data, cards: [...data.cards, ...more.data.cards], next_cursor: more.data.next_cursor };
  }
  return { ...first, data };
}

function useShipped(ws: number, repo: number | null, view: View, owner: string | null, generation: number) {
  const [cards, setCards] = useState<Card[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchPage = useCallback(
    async (cursor: string | null) => {
      setBusy(true);
      const query = { column: 'shipped' as const, limit: SHIPPED_PAGE, ...viewQuery(repo, view, owner), ...(cursor ? { cursor } : {}) };
      try {
        const { data, error: e, response } = await api.GET('/api/workspaces/{ws_id}/board', {
          params: { path: { ws_id: ws }, query },
        });
        if (data) {
          setCards((prev) => (cursor ? [...prev, ...data.cards] : data.cards));
          setNext(data.next_cursor);
          setError(null);
        } else setError(errorMessage(e, response.status));
      } catch {
        setError(errorMessage(null));
      }
      setBusy(false);
    },
    [ws, repo, view, owner],
  );
  useEffect(() => {
    void fetchPage(null);
  }, [fetchPage, generation]);
  return { cards, next, busy, error, more: () => next && fetchPage(next) };
}

const LOADING = 'px-5 py-4 text-ink-muted';
const FILTER = 'inline-flex h-[26px] cursor-pointer items-center gap-1.5 rounded-[6px] border px-2.5 text-[12px] coarse:min-h-10';
const FILTER_ON = 'border-transparent bg-accent-faded font-semibold text-accent-display';
const FILTER_OFF = 'border-hairline bg-page text-ink-body hover:border-hairline-strong';

/** Plans by owner (`owner:` as written), and **Mine** when the user has linked GitHub. Shown only
 *  when some plan names an owner. */
function OwnerFilter({ owners, owner, mine, onChange }: { owners: Board['owners']; owner: string | null; mine: string | null; onChange: (owner: string | null) => void }) {
  const isMine = !!mine && !!owner && owner.toLowerCase() === mine.toLowerCase();
  const choices = owners.map((o) => o.login);
  if (owner && !choices.some((c) => c.toLowerCase() === owner.toLowerCase())) choices.push(owner);
  return (
    <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Owner">
      {mine && (
        <button type="button" className={cn(FILTER, isMine ? FILTER_ON : FILTER_OFF)} aria-pressed={isMine} onClick={() => onChange(isMine ? null : mine)}>
          Mine
        </button>
      )}
      <Select lead={<span className="text-ink-muted">owner</span>} aria-label="Owner" value={owner ?? ''} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">anyone</option>
        {choices.map((login) => (
          <option key={login} value={login}>
            {login}
          </option>
        ))}
      </Select>
    </div>
  );
}

function SyncMenu({ repos, owner, onRefreshed }: { repos: BoardRepo[]; owner: boolean; onRefreshed: () => void }) {
  const [note, setNote] = useState<string | null>(null);
  const bad = repos.find((r) => r.sync.state === 'error');
  const pending = repos.some((r) => r.sync.state === 'pending');
  const oldest = repos
    .map((r) => r.sync.synced_at)
    .filter((t): t is string => !!t)
    .sort()[0];
  const summary = bad ? 'sync error' : pending ? 'syncing…' : `synced ${ago(oldest)}`;
  async function refresh(r: BoardRepo) {
    setNote(null);
    const { data, error, response } = await api.POST('/api/repos/{repo_id}/refresh', {
      params: { path: { repo_id: r.id } },
    });
    if (data) {
      setNote(`Refresh queued for ${r.full_name}.`);
      window.setTimeout(onRefreshed, 3000);
    } else setNote(errorMessage(error, response.status));
  }
  return (
    <details className="relative">
      <summary
        className="inline-flex h-[30px] cursor-pointer list-none items-center gap-1.5 rounded px-1.5 text-[12px] text-ink-muted hover:bg-surface [&::-webkit-details-marker]:hidden"
        aria-label={`Sync: ${summary}`}
      >
        <span className={pulseClass(bad ? 'error' : pending ? 'pending' : 'ok')} aria-hidden="true" />
        <span>{summary}</span>
        <Icon name="chev" small />
      </summary>
      <div
        className="absolute left-0 top-[calc(100%+6px)] z-30 min-w-[280px] max-w-[min(420px,calc(100vw-32px))] rounded-lg border border-hairline-strong bg-page p-2 text-ink-body"
        role="group"
        aria-label="Sync state per meta repo"
      >
        <ul className="m-0 list-none p-0">
          {repos.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-2 border-t border-hairline px-1 py-1.5 first:border-t-0">
              <span className={pulseClass(r.sync.state)} aria-hidden="true" />
              <span className={cn(mono, 'font-semibold text-ink-display [overflow-wrap:anywhere]')}>{r.full_name}</span>
              <span className="text-ink-muted">
                {r.sync.state === 'ok' && `synced ${ago(r.sync.synced_at)}`}
                {r.sync.state === 'pending' && 'first sync pending'}
                {r.sync.state === 'error' && (r.sync.settings_error?.message ?? r.sync.error ?? 'sync failed')}
              </span>
              {owner && (
                <button
                  type="button"
                  className={buttonClass('secondary', 'icon', 'ml-auto')}
                  aria-label={`Refresh ${r.full_name}`}
                  onClick={() => refresh(r)}
                >
                  <Icon name="refresh" />
                </button>
              )}
            </li>
          ))}
        </ul>
        {note && (
          <p className="mx-1 mb-0.5 mt-1.5 text-[12px]" role="status">
            {note}
          </p>
        )}
      </div>
    </details>
  );
}

function Unreported({ repos }: { repos: BoardRepo[] }) {
  return <>{repos.map((repo) => <span key={repo.id} className="text-[12px] text-ink-body" aria-label={`${repo.product ?? repo.full_name}: ${repo.capacity?.count ?? repo.unreported.count} in flight`} title={repo.unreported.keys.join(', ')}>
    {repos.length > 1 && `${repo.product ?? repo.full_name}: `}
    {repo.capacity?.count ?? repo.unreported.count} in flight
    {(repo.capacity?.hosts ?? [{ name: 'local', used: null, ceiling: repo.unreported.cap }]).map((host) => <span key={host.name}> · {host.name} {host.used ?? '?'}/{host.ceiling ?? '?'}</span>)}
  </span>)}</>;
}

function SearchResults({ ws, q }: { ws: number; q: string }) {
  const [res] = useLoad(
    () => api.GET('/api/workspaces/{ws_id}/search', { params: { path: { ws_id: ws }, query: { q, limit: 50 } } }),
    `${ws}:${q}`,
  );
  if (res.state === 'loading') return <p className={LOADING}>Searching…</p>;
  if (res.state === 'error')
    return (
      <Callout as="p" role="alert">
        {res.message}
      </Callout>
    );
  const hits = res.data.results;
  return (
    <section className="px-5 pb-6 pt-1 phone:px-3 phone:pb-4" aria-label="Search results">
      <p className="text-ink-muted">
        {hits.length === 0 ? `Nothing matches “${q}”.` : `${hits.length}${res.data.next_cursor ? '+' : ''} result${hits.length === 1 ? '' : 's'} for “${q}”`}
      </p>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-2.5 phone:grid-cols-[minmax(0,1fr)]">
        {hits.map((h) => (
          <div key={cardKey(h.card)} className="grid min-w-0 content-start gap-1">
            <CardView card={h.card} ws={ws} showRepo />
            {h.match === 'content' && <p className="mx-1 my-0 text-[11.5px] text-ink-muted [overflow-wrap:anywhere]">…{h.snippet}…</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

export function BoardPage() {
  const { ws: wsParam } = useParams();
  const ws = Number(wsParam);
  const { me } = useSession();
  const workspace = findWorkspace(me, ws);
  const [params, setParams] = useSearchParams();
  const repoParam = Number(params.get('repo')) || null;
  const show = (params.get('show') || 'all') as View;
  const ownerParam = params.get('owner') || null;
  const myLogin = me.github.linked ? me.github.login : null;
  const q = params.get('q') ?? '';
  const activeCol = (params.get('col') as ColumnKey | null) ?? 'inbox';
  const [generation, setGeneration] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(q);

  useEffect(() => {
    if (workspace) rememberWorkspace(ws);
  }, [ws, workspace]);
  useEffect(() => setDraft(q), [q]);
  useEffect(() => {
    if (params.get('search') === '1') searchRef.current?.focus();
  }, [params]);

  // Another view keeps the board on screen until its cards come (no "Loading" flash per filter).
  const [board, reloadBoard] = useLoad(
    () => loadBoard(ws, repoParam, show, ownerParam),
    `${ws}:${repoParam}:${show}:${ownerParam}`,
    `${ws}:${repoParam}`,
  );
  const [repos] = useLoad(() => api.GET('/api/workspaces/{ws_id}/repos', { params: { path: { ws_id: ws } } }), `${ws}`);
  const shipped = useShipped(ws, repoParam, show, ownerParam, generation);
  const agents = useAgents(ws, workspace?.role === 'owner');
  const reload = useCallback(() => {
    reloadBoard();
    setGeneration((g) => g + 1);
  }, [reloadBoard]);

  const set = useCallback(
    (changes: Record<string, string | null>) => {
      const next = new URLSearchParams(params);
      for (const [k, v] of Object.entries(changes)) {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      }
      next.delete('search');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  // Search as you type: the query goes into the URL (so it survives reload) after a short pause.
  useEffect(() => {
    if (draft === q) return;
    const t = window.setTimeout(() => set({ q: draft.trim() || null }), 250);
    return () => window.clearTimeout(t);
  }, [draft, q, set]);

  const touch = useRef<{ x: number; y: number } | null>(null);
  const data = board.state === 'ok' ? board.data : null;
  const columns = useMemo(() => data?.columns ?? [], [data]);
  const roles = useMemo(() => Object.fromEntries((data?.repos ?? []).map((r) => [r.id, r.labels])), [data]);

  if (!workspace) return <NotFoundPage />;
  const owner = workspace.role === 'owner';
  const repoList: MetaRepo[] = repos.state === 'ok' ? repos.data.repos : [];

  function swipe(e: TouchEvent, end: boolean) {
    const t = e.changedTouches[0];
    if (!t) return;
    if (!end) {
      touch.current = { x: t.clientX, y: t.clientY };
      return;
    }
    const start = touch.current;
    touch.current = null;
    if (!start) return;
    const dx = t.clientX - start.x;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(t.clientY - start.y) * 1.5) return;
    const i = columns.findIndex((c) => c.key === activeCol);
    const next = columns[i + (dx < 0 ? 1 : -1)];
    if (next) set({ col: next.key });
  }

  const controls = (
    <>
      {repoList.length > 1 && (
        <Select
          lead={<span className="text-ink-muted">repo</span>}
          aria-label="Meta repo"
          value={repoParam ?? ''}
          onChange={(e) => set({ repo: e.target.value || null })}
        >
          <option value="">all ({repoList.length})</option>
          {repoList.map((r) => (
            <option key={r.id} value={r.id}>
              {r.full_name}
            </option>
          ))}
        </Select>
      )}
      {repoList.length === 1 && repoList[0] && (
        <span className={selectBox}>
          <span className="text-ink-muted">repo</span>
          <span className={cn(mono, 'truncate')}>{repoList[0].full_name}</span>
        </span>
      )}
      {data && data.repos.length > 0 && <SyncMenu repos={data.repos} owner={owner} onRefreshed={reload} />}
    </>
  );

  return (
    <AgentsContext.Provider value={agents}>
    <Shell ws={ws} controls={controls} onSearch={() => searchRef.current?.focus()}>
      <LabelRolesByRepo.Provider value={roles}>
      <div className="flex flex-wrap items-center gap-2 px-5 pb-3 pt-3.5 phone:px-3 phone:pb-2 phone:pt-3">
        <h1 className="m-0 mr-2.5 text-[15px] font-semibold tracking-[-0.015em]">Board</h1>
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Show">
          {(data?.views ?? []).map((v) => (
            <button
              key={v.key}
              type="button"
              className={cn(FILTER, show === v.key ? FILTER_ON : FILTER_OFF)}
              aria-pressed={show === v.key}
              onClick={() => set({ show: v.key === 'all' ? null : v.key })}
            >
              {v.name} <span className="font-mono text-[11px]">{v.count}</span>
            </button>
          ))}
        </div>
        {data && data.owners.length > 0 && (
          <OwnerFilter owners={data.owners} owner={ownerParam} mine={myLogin} onChange={(o) => set({ owner: o })} />
        )}
        {data && <Unreported repos={data.repos} />}
        <label className="inline-flex h-[30px] min-w-0 flex-[0_1_240px] items-center gap-1.5 rounded border border-hairline px-2.5 text-ink-muted focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-focus-ring phone:order-10 phone:h-10 phone:flex-[1_1_100%]">
          <Icon name="search" small />
          <span className="sr-only">Search cards</span>
          <input
            className="w-full min-w-0 border-0 bg-transparent text-[12.5px] text-ink-display focus:outline-none focus-visible:outline-none coarse:text-[16px]"
            ref={searchRef}
            type="search"
            placeholder="Search cards and files"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </label>
        <span className="ml-auto flex items-center gap-3 text-[11.5px] text-ink-muted narrow:hidden [&>span]:inline-flex [&>span]:items-center [&>span]:gap-[5px]" aria-hidden="true">
          <span>
            <RunMark state="pass" /> checks pass
          </span>
          <span>
            <RunMark state="pending" /> pending
          </span>
          <span>
            <RunMark state="fail" /> failing
          </span>
        </span>
      </div>

      {q.length === 1 && <p className={LOADING}>Type at least two characters to search.</p>}
      {q.length >= 2 ? (
        <SearchResults ws={ws} q={q} />
      ) : board.state === 'loading' ? (
        <p className={LOADING}>Loading the board…</p>
      ) : board.state === 'error' ? (
        board.status === 404 ? (
          <NotFoundPage bare />
        ) : (
          <Callout as="p" role="alert" className="items-center">
            {board.message}{' '}
            <button type="button" className={buttonClass()} onClick={reload}>
              Try again
            </button>
          </Callout>
        )
      ) : data && data.repos.length === 0 ? (
        <EmptySection heading={<h2 className="m-0 mb-1.5 text-[18px]">No meta repos yet</h2>}>
          <p className="text-ink-muted">
            {owner
              ? 'Add a meta repo in the workspace settings to see its plans, bugs and pull requests here.'
              : 'An owner of this workspace has not added a meta repo yet.'}
          </p>
        </EmptySection>
      ) : (
        <>
          {/* Phone only: the strip scrolls sideways inside itself; its right edge fades to say there is more. */}
          <div
            className="sticky top-0 hidden gap-0.5 overflow-x-auto border-b border-hairline px-2 [scrollbar-width:none] phone:flex phone:[mask-image:linear-gradient(to_right,currentColor_calc(100%-var(--fade-edge)),transparent)] [&::-webkit-scrollbar]:hidden"
            role="tablist"
            aria-label="Columns"
          >
            {columns.map((c) => (
              <button
                key={c.key}
                type="button"
                role="tab"
                className={cn(
                  '-mb-px inline-flex min-h-11 cursor-pointer items-baseline gap-[5px] whitespace-nowrap border-0 border-b-2 bg-transparent px-2 pb-2.5 pt-3 text-[12.5px]',
                  activeCol === c.key
                    ? cn('font-semibold text-ink-display', COLUMN_TONE[c.key].border)
                    : 'border-transparent font-medium text-ink-muted',
                )}
                aria-selected={activeCol === c.key}
                aria-controls={`col-${c.key}`}
                onClick={() => set({ col: c.key })}
              >
                {c.name} <span className="font-mono text-[11px]">{c.count}</span>
              </button>
            ))}
          </div>
          <div
            className="grid flex-1 snap-x snap-proximity auto-cols-[minmax(188px,1fr)] grid-flow-col items-start gap-[18px] overflow-x-auto scroll-px-5 px-5 pb-6 pt-1 phone:block phone:overflow-visible phone:px-3 phone:pb-4 phone:pt-2"
            onTouchStart={(e) => swipe(e, false)}
            onTouchEnd={(e) => swipe(e, true)}
          >
            {columns.map((c) => {
              const isShipped = c.key === 'shipped';
              const cards = isShipped ? shipped.cards : (data?.cards ?? []).filter((x) => x.column === c.key);
              const count = c.count;
              return (
                <section
                  key={c.key}
                  id={`col-${c.key}`}
                  className={`col col-${c.key} min-w-0 snap-start phone:[&:not([data-active])]:hidden`}
                  data-col={c.key}
                  data-active={activeCol === c.key ? '' : undefined}
                  aria-labelledby={`col-h-${c.key}`}
                >
                  <header className={cn('flex items-center gap-2 border-t-2 px-0.5 pb-2.5 pt-[9px] phone:hidden', COLUMN_TONE[c.key].border)}>
                    <h2 id={`col-h-${c.key}`} className="m-0 text-[11px] font-semibold uppercase tracking-[0.09em]">
                      {c.name}
                    </h2>
                    <span className="font-mono text-[11px] text-ink-muted" aria-label={`${count} cards`}>
                      {count}
                    </span>
                  </header>
                  <div className="flex flex-col gap-2">
                    {cards.length === 0 && !(isShipped && shipped.busy) && (
                      <EmptyState size="sm">{show === 'all' && !ownerParam ? 'Nothing here' : 'Nothing here for this filter'}</EmptyState>
                    )}
                    {cards.map((card) =>
                      // Bugs filter: an open batch's bugs (the API's `issues`) are listed beneath its
                      // card. An archived batch stays plain: its bugs are cards of their own (with the
                      // B-NNN badge), so listing them again would show them twice.
                      show === 'bugs' && card.kind === 'batch' && card.location === 'plans' && card.issues.length > 0 ? (
                        <div key={cardKey(card)} className="batch-group flex min-w-0 flex-col gap-1">
                          <CardView card={card} ws={ws} showRepo={data !== null && data.repos.length > 1} bugsBeneath />
                          <BatchBugs card={card} />
                        </div>
                      ) : (
                        <CardView key={cardKey(card)} card={card} ws={ws} showRepo={data !== null && data.repos.length > 1} />
                      ),
                    )}
                    {isShipped && shipped.error && (
                      <Callout as="p" role="alert">
                        {shipped.error}
                      </Callout>
                    )}
                    {isShipped && (shipped.next || shipped.busy) && (
                      <button
                        type="button"
                        className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border border-dashed border-hairline-strong bg-transparent px-3 py-[9px] text-[12px] text-ink-muted hover:border-status-shipped hover:text-ink-display [&_b]:text-ink-display"
                        disabled={shipped.busy}
                        onClick={() => void shipped.more()}
                      >
                        <span>
                          Shipped · <b>{shipped.cards.length}</b> of {c.count}
                        </span>
                        <span>{shipped.busy ? 'loading…' : 'show more'}</span>
                      </button>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}
      </LabelRolesByRepo.Provider>
    </Shell>
    </AgentsContext.Provider>
  );
}
